import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../Includes/Navbar/Navbar";
import Sidebar from "../../Includes/Sidebar/Sidebar";
import UserServices from "../../../UserServices/UserServices";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from "recharts";

const fetchAttendance = async ({ start_date, end_date, branch_id = "", gender = "", start_time = "", end_time = "", limit = 1000, page = 1 }) => {
  try {
    const resp = await UserServices.getClientAttendence(
      "Member",
      "1",
      branch_id,
      {
        sdate: start_date,
        edate: end_date,
        start_time,
        end_time,
        gender,
      },
      "",
      limit,
      page
    );
    if (resp.status === 200) {
      return resp.data.data?.data || [];
    }
    return [];
  } catch (e) {
    return [];
  }
};

const formatBranch = (uid = "") => { const m = uid.match(/^([A-Za-z]+)(\d+)/); return m ? `${m[1]}-${m[2]}` : uid; };
const COLORS = ["#0088FE", "#FF6384", "#00C49F", "#FFBB28"]; // male, female, etc

export default function FootfallCombinedReport() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [filters, setFilters] = useState({ start_date: today, end_date: today, branch_id: "", gender: "", start_time: "", end_time: "" });
  const [tmp, setTmp] = useState({ ...filters });
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(false);

  const onTmpChange = (e) => {
    const { name, value } = e.target;
    setTmp((g) => ({ ...g, [name]: value }));
  };
  const apply = () => setFilters({ ...tmp });

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const multi = filters.start_date !== filters.end_date;
        if (!multi) {
          setRaw(await fetchAttendance({ ...filters, limit: 1000 }));
        } else {
          const days = (() => { const out=[]; let cur=new Date(filters.start_date); const end=new Date(filters.end_date); while(cur<=end){ out.push(cur.toISOString().slice(0,10)); cur.setDate(cur.getDate()+1);} return out; })();
          const res = await Promise.all(days.map(d => fetchAttendance({ ...filters, start_date: d, end_date: d, limit: 1000 })));
          setRaw(res.flat());
        }
      } catch(_){} finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [filters]);

  const totals = useMemo(() => {
    const total = raw.length;
    const male = raw.filter(r => (r.gender||"").toLowerCase()==="male").length;
    const female = raw.filter(r => (r.gender||"").toLowerCase()==="female").length;
    // busiest hour
    const hourMap = new Map();
    raw.forEach(r => { const h=(r.checkin_time_24h||"00:00:00").slice(0,2); hourMap.set(h,(hourMap.get(h)||0)+1);});
    let busiestHour = "-"; let max=0; hourMap.forEach((v,k)=>{ if(v>max){max=v; busiestHour = (()=>{ const n=Number(k); const s=n>=12?"PM":"AM"; const v=n%12||12; return `${v}:00 ${s}`; })(); }});
    // branch totals
    const branchTotals = {};
    raw.forEach(r=>{ const b=formatBranch(r.attendee?.uid||""); branchTotals[b]=(branchTotals[b]||0)+1; });
    return { total, male, female, busiestHour, branchTotals };
  }, [raw]);

  const genderPie = useMemo(() => ([ { name: "Male", value: totals.male||0 }, { name: "Female", value: totals.female||0 } ]), [totals]);

  return (
    <div className='wrapper'>
      <Navbar/>
      <Sidebar/>
      <main className="page-content customer-contnent">
        <div className="site-main-design">
      <div className="card mb-3"><div className="card-body">
        <form className="row g-2" aria-label="Footfall global filters">
          <div className="col-12 col-md-3"><label className="form-label">Start date</label><input name="start_date" type="date" className="form-control" value={tmp.start_date} onChange={onTmpChange} /></div>
          <div className="col-12 col-md-3"><label className="form-label">End date</label><input name="end_date" type="date" className="form-control" value={tmp.end_date} onChange={onTmpChange} /></div>
          <div className="col-12 col-md-2"><label className="form-label">Branch</label>
            <select name="branch_id" className="form-select" value={tmp.branch_id} onChange={onTmpChange}>
              <option value="">All</option>
              <option value="15">F-11 (id=15)</option>
              <option value="1">G-13 (id=1)</option>
            </select>
          </div>
          <div className="col-12 col-md-2"><label className="form-label">Gender</label><select name="gender" className="form-select" value={tmp.gender} onChange={onTmpChange}><option value="">All</option><option value="Male">Male</option><option value="Female">Female</option></select></div>
          <div className="col-6 col-md-1"><label className="form-label">From</label><input name="start_time" type="time" className="form-control" value={tmp.start_time} onChange={onTmpChange} /></div>
          <div className="col-6 col-md-1"><label className="form-label">To</label><input name="end_time" type="time" className="form-control" value={tmp.end_time} onChange={onTmpChange} /></div>
          <div className="col-12 col-md-2 d-flex align-items-end"><button type="button" className="btn btn-primary w-100" onClick={apply}>Go</button></div>
        </form>
      </div></div>

      {/* Tiles */}
      <div className="row g-3">
        <div className="col-6 col-lg-3"><div className="card h-100"><div className="card-body"><div className="text-muted">Total</div><div className="fs-3 fw-bold">{totals.total||0}</div></div></div></div>
        <div className="col-6 col-lg-3"><div className="card h-100"><div className="card-body"><div className="text-muted">Male</div><div className="fs-4 fw-bold">{totals.male||0}</div></div></div></div>
        <div className="col-6 col-lg-3"><div className="card h-100"><div className="card-body"><div className="text-muted">Female</div><div className="fs-4 fw-bold">{totals.female||0}</div></div></div></div>
        <div className="col-6 col-lg-3"><div className="card h-100"><div className="card-body"><div className="text-muted">Busiest Hour</div><div className="fs-4 fw-bold">{totals.busiestHour}</div></div></div></div>
      </div>

      <div className="row g-3 mt-1">
        <div className="col-12 col-lg-4">
          <div className="card h-100"><div className="card-body">
            <h6 className="mb-2">Gender Split</h6>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={genderPie} dataKey="value" nameKey="name" outerRadius={70} label>
                    {genderPie.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div></div>
        </div>
        <div className="col-12 col-lg-8">
          <div className="card h-100"><div className="card-body">
            <h6 className="mb-2">Branch Totals</h6>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={Object.keys(totals.branchTotals||{}).map(b=>({ branch:b, count: totals.branchTotals[b] }))} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="branch" interval={0} tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0088FE" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div></div>
        </div>
      </div>
      </div>
      </main>
    </div>
  );
}


